import {
  OFFER_REACTIVATE_FOR_ANNUAL_DISCOUNT,
  OFFER_REACTIVATE_FOR_BENEFITS,
  REACTIVATION_GRACE_PERIOD_MONTHS,
} from '../types';
import ReactivateForBenefitsImage from '../../assets/images/reactivate-for-benefits.png';
import ReactivateAccountImage from '../../assets/images/reactivate-account.png';
import { GrowegyCheckbox } from '../../program/components/GrowegyCheckbox';
import { nonBreakableSpace } from '../../util/utils';
import { Button } from 'react-bootstrap';
import React, { useMemo, useState } from 'react';
import { BILLING_PLANS, BillingPlan } from '../billingPlans';
import { useReactivateMutation } from '../hooks/useReactivateMutation';
import { ColoredSelect } from '../../program/components/ColoredSelect';
import { Pricing } from './Pricing';
import { useFetchedBillingInvoices } from '../hooks/useFetchedBillingInvoices';
import _ from 'lodash';
import { getInvoiceMaxEnd } from '../utils';
import moment from 'moment';
import { NewTabLink } from '../../commons/components/NewTabLink';

export const Reactivation = (props: { billingPlan: BillingPlan; offer: string | null }) => {
  const { billingPlan, offer } = props;
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);
  const [
    billingIsRecurringAndImmediateAcknowledged,
    setBillingIsRecurringAndImmediateAcknowledged,
  ] = useState(false);
  const { mutate: reactivateMutation, status: activateStatus } = useReactivateMutation();
  const [showError, setShowError] = useState(false);
  const [selectedBillingPlan, setSelectedBillingPlan] = useState(billingPlan);
  const {
    query: { data: invoices, status: invoicesStatus },
    queryStatusComponent: invoicesStatusComponent,
  } = useFetchedBillingInvoices();
  const [now] = useState(new Date());
  const hasRecentlyCanceled = useMemo(() => {
    if (!invoices) return false;
    if (invoices.length === 0) return false;
    const maxInvoiceEnd = _.max(invoices.map((invoice) => getInvoiceMaxEnd(invoice)));
    if (!maxInvoiceEnd) return false;
    return (
      moment(maxInvoiceEnd).add(REACTIVATION_GRACE_PERIOD_MONTHS, 'months').valueOf() >
      now.valueOf()
    );
  }, [now, invoices]);

  return (
    <div className="billing__reactivation-offer-container">
      <p className="growegy-font16--weight500 growegy-font-em">
        {!offer
          ? 'Reactivate your Growegy account now!'
          : 'Benefit from our limited-time offer — exclusive to selected customers'}
      </p>

      {offer && <div>Reactivate your Growegy account before the offer expires.</div>}
      <div className="release-prompt__content-container">
        <img
          src={
            offer === OFFER_REACTIVATE_FOR_BENEFITS
              ? ReactivateForBenefitsImage
              : ReactivateAccountImage
          }
          alt=""
          width={300}
        />
        <div>
          <div>Here’s what’s waiting for you:</div>
          <div>
            ✅ Freshen up your credit profile with new tradeline reports! <b>Net30 terms</b> and
            monthly credit reporting.
          </div>
          {offer === OFFER_REACTIVATE_FOR_ANNUAL_DISCOUNT && (
            <>
              <div>
                ✅ <strong>Get 10% off</strong> for your first annual plan installment (pay just
                $180 instead of $200)!
              </div>
              <div>
                ✅ Loyalty benefits fast track! Start receiving benefits after your first payment!
              </div>
            </>
          )}
          <div>
            ✅ All-in-one business suite: business plan builder, content AI generation, and
            management tools.
          </div>
          {offer === OFFER_REACTIVATE_FOR_BENEFITS ? (
            <>
              <div>
                ✅ Loyalty benefits earned from your previous subscription that weren't redeemed
                will become active after the first payment.
              </div>
              <div>
                ✅ Plus, you’ll retain your progress towards any benefits that you have not earned
                yet.
              </div>
            </>
          ) : (
            <div>✅ Awesome loyalty program benefits!</div>
          )}
        </div>
      </div>
      <div>
        <span className="growegy-font16--weight500">
          {offer === null ? (
            <>
              <span>Choose your plan: </span>
              <div className="d-inline-block">
                <ColoredSelect
                  dataTestPrefix="reactivation__plan"
                  initialValue={selectedBillingPlan}
                  coloredItems={BILLING_PLANS}
                  onValueChange={setSelectedBillingPlan}
                  isSearchable={false}
                />
              </div>
            </>
          ) : (
            <>
              Your plan: {selectedBillingPlan.displayName} plan ·{' '}
              {offer === OFFER_REACTIVATE_FOR_ANNUAL_DISCOUNT
                ? '$580 / 1st year (3 installments)'
                : selectedBillingPlan.scheduleDescription}
            </>
          )}
        </span>
        <br />
        <span style={{ fontStyle: 'italic' }}>Start using now and pay within 30 days.</span>
      </div>
      {!offer && <Pricing />}
      {invoicesStatus === 'loading'
        ? invoicesStatusComponent
        : !hasRecentlyCanceled && (
            <>
              <div>
                <GrowegyCheckbox
                  dataTestPrefix="billing-reactivation"
                  checked={termsAndConditionsAccepted}
                  onChange={(e) => setTermsAndConditionsAccepted(e.target.checked)}
                >
                  I understand and agree to pay each invoice manually each billing cycle. While card
                  details can be saved, there are no auto payments. Invoices and reminders are sent
                  electronically to the email used on the application. You can update the email
                  address after approval.
                </GrowegyCheckbox>
                {showError && !termsAndConditionsAccepted && (
                  <span className="growegy-font14--error">This field is required.</span>
                )}
              </div>
              <div>
                <GrowegyCheckbox
                  dataTestPrefix="billing-reactivation"
                  checked={billingIsRecurringAndImmediateAcknowledged}
                  onChange={(e) => setBillingIsRecurringAndImmediateAcknowledged(e.target.checked)}
                >
                  <span>
                    I have read and agreed to the{' '}
                    <NewTabLink href="https://www.growegy.com/terms">
                      Terms{nonBreakableSpace}of{nonBreakableSpace}Service
                    </NewTabLink>{' '}
                    and{' '}
                    <NewTabLink href="https://www.growegy.com/privacy-policy">
                      Privacy{nonBreakableSpace}Policy
                    </NewTabLink>
                  </span>
                </GrowegyCheckbox>
                {showError && !billingIsRecurringAndImmediateAcknowledged && (
                  <span className="growegy-font14--error">This field is required.</span>
                )}
              </div>
            </>
          )}
      <Button
        variant="primary"
        onClick={() => {
          if (hasRecentlyCanceled) {
            reactivateMutation({
              offer,
              termsAndConditionsAccepted: true,
              billingIsRecurringAndImmediateAcknowledged: true,
              billingPlan: selectedBillingPlan.name,
            });
          } else if (!termsAndConditionsAccepted || !billingIsRecurringAndImmediateAcknowledged) {
            setShowError(true);
          } else {
            reactivateMutation({
              offer,
              termsAndConditionsAccepted,
              billingIsRecurringAndImmediateAcknowledged,
              billingPlan: selectedBillingPlan.name,
            });
          }
        }}
        disabled={activateStatus === 'loading'}
      >
        {activateStatus !== 'loading' ? 'Reactivate' : 'Reactivating...'}
      </Button>
    </div>
  );
};
